import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../api/client';
import { createPost, listPosts, type PostSummary } from '../api/posts';
import { AppShell } from '../components/layout/AppShell';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
} from '../components/ui';
import type { BadgeTone } from '../components/ui';
import type { PostStatus } from '../api/posts';
import './StoriesPage.css';

type State = 'loading' | 'ready' | 'error';

const TONES: Record<PostStatus, BadgeTone> = {
  DRAFT: 'neutral',
  PENDING_REVIEW: 'accent',
  REJECTED: 'warning',
  PUBLISHED: 'success',
  UNPUBLISHED: 'warning',
  HIDDEN: 'warning',
};

const LABELS: Record<PostStatus, string> = {
  DRAFT: 'Draft',
  PENDING_REVIEW: 'Waiting for review',
  REJECTED: 'Not approved',
  PUBLISHED: 'Published',
  UNPUBLISHED: 'Taken down',
  HIDDEN: 'Taken down',
};

/** `/stories` — everything the user has written. */
export function StoriesPage() {
  const navigate = useNavigate();
  const [state, setState] = useState<State>('loading');
  const [posts, setPosts] = useState<PostSummary[]>([]);
  const [title, setTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const load = useCallback(async () => {
    try {
      setPosts(await listPosts());
      setState('ready');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
      setState('error');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const start = async () => {
    if (!title.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const post = await createPost({ title: title.trim() });
      navigate(`/stories/${post.id}/edit`);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
    } finally {
      setCreating(false);
    }
  };

  const header = (
    <div className="sc-page-header">
      <div>
        <h1>Stories</h1>
        <p>What you found, and what it means. A story reads on its own.</p>
      </div>
    </div>
  );

  if (state === 'loading') {
    return (
      <AppShell header={header}>
        <LoadingState label="Loading your stories…" />
      </AppShell>
    );
  }

  if (state === 'error') {
    return (
      <AppShell header={header}>
        <ErrorState
          title="We could not load your stories"
          description={error?.message}
          requestId={error?.requestId}
          onRetry={() => void load()}
        />
      </AppShell>
    );
  }

  return (
    <AppShell header={header}>
      <Card title="Start a story" description="Give it a title. You can change it later.">
        {error && (
          <Alert tone="warning" title="That story could not be started">
            {error.message}
          </Alert>
        )}
        <div className="sc-stories__start">
          <Input
            label="Title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="When does coffee sell?"
          />
          <Button isLoading={creating} disabled={!title.trim()} onClick={() => void start()}>
            Start writing
          </Button>
        </div>
      </Card>

      {posts.length === 0 ? (
        <EmptyState
          title="No stories yet"
          description="A story is where a dashboard becomes something someone else can follow."
        />
      ) : (
        <div className="sc-stories">
          {posts.map((post) => (
            <Card key={post.id} title={post.title} description={post.subtitle}>
              <p className="sc-stories__meta">
                <Badge tone={TONES[post.status]} srPrefix="Status">
                  {LABELS[post.status]}
                </Badge>{' '}
                {post.block_count} block{post.block_count === 1 ? '' : 's'}
                {post.published_version !== null && ` · version ${post.published_version} is live`}
                {post.draft_version !== null && ` · version ${post.draft_version} in draft`}
              </p>
              <div className="sc-stories__actions">
                <Link to={`/stories/${post.id}`}>Read</Link>
                <Link to={`/stories/${post.id}/edit`}>Edit</Link>
              </div>
            </Card>
          ))}
        </div>
      )}
    </AppShell>
  );
}
